import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import HubPageLayout from '../components/HubPageLayout';
import FramedPanel from '../components/FramedPanel';
import SOCIAL_MEDIA_MATRIX, { PLATFORMS, SEVERITY_STYLES } from './data/socialMediaMatrix';

// Social Media IQ — which scams hit which platform and how hard. The Abuse
// Playbook (the tactics those scams run on) is its own page, linked below.
//
// 9-22 mockups: the matrix is read per scam, not as a grid. Each scam gets a
// row of three columns - the scam, the platforms it hits (dot coloured by that
// platform's rating) and an Overall Threat Index bar. The data is unchanged
// (data/socialMediaMatrix.js); only the presentation is.

const SOCIAL_BANNER = '/images/social-media-iq/social-media-banner.jpeg';

// Ascending order; the overall index is the highest level any platform holds.
const SEVERITY_ORDER = ['Elevated', 'High', 'Severe'];
const SEVERITY_COLOR = { Severe: '#B02418', High: '#D97706', Elevated: '#EAB308' };

const overallIndex = (ratings) =>
  ratings.reduce(
    (best, r) =>
      r && (!best || SEVERITY_ORDER.indexOf(r) > SEVERITY_ORDER.indexOf(best)) ? r : best,
    null
  );

// Framed hero with the caption inside the banner (mockup: "Lairs" corrected).
const PageHeader = () => (
  <FramedPanel title="Social Media – Predators on the hunt.">
    <div className="relative px-6 pt-10 pb-6">
      <img
        src={SOCIAL_BANNER}
        alt="Logos of the major social media platforms"
        className="w-full max-h-[320px] object-cover block"
      />
      <p className="absolute left-12 bottom-12 text-white text-2xl xl:text-3xl font-bold drop-shadow-md mb-0">
        Liars. Fraudsters. Abusers.
      </p>
    </div>
  </FramedPanel>
);

const Legend = () => (
  <div className="flex items-center gap-4 flex-wrap text-sm">
    {SEVERITY_ORDER.slice().reverse().map((level) => (
      <span key={level} className="flex items-center gap-1.5">
        <span
          className="inline-block w-3 h-3 rounded-full"
          style={{ backgroundColor: SEVERITY_COLOR[level] }}
        />
        <span className="text-black">{SEVERITY_STYLES[level].label}</span>
      </span>
    ))}
  </div>
);

// Platforms the scam is rated on, dot coloured by that platform's rating.
const PlatformList = ({ ratings }) => {
  const rated = PLATFORMS.map((platform, i) => ({ platform, rating: ratings[i] })).filter(
    (x) => x.rating
  );
  if (!rated.length) {
    return <p className="text-sm text-gray-500 italic">Not rated in the source matrix.</p>;
  }
  return (
    <ul className="grid grid-cols-2 gap-x-6 gap-y-2">
      {rated.map(({ platform, rating }) => (
        <li key={platform} className="flex items-center gap-3 text-lg text-black">
          <span
            className="inline-block w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: SEVERITY_COLOR[rating] }}
            title={`${SEVERITY_STYLES[rating].label} on ${platform}`}
          />
          {platform}
        </li>
      ))}
    </ul>
  );
};

// Three-row bar chart: the share of platforms at each level. The overall
// rating (highest level present) is the bold row.
const ThreatIndex = ({ ratings }) => {
  const rated = ratings.filter(Boolean);
  const overall = overallIndex(ratings);
  return (
    <div
      className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 items-center"
      role="img"
      aria-label={overall ? `Overall threat index: ${overall}` : 'Overall threat index: not rated'}
    >
      {SEVERITY_ORDER.map((level) => {
        const share = rated.length
          ? rated.filter((r) => r === level).length / PLATFORMS.length
          : 0;
        const isOverall = overall === level;
        return (
          <React.Fragment key={level}>
            <span
              className={`text-base text-right ${
                isOverall ? 'font-bold text-black' : 'text-gray-500'
              }`}
            >
              {level}
            </span>
            <div className="h-3 border-l border-gray-400 bg-gray-100">
              <div
                className="h-full"
                style={{
                  width: `${Math.round(share * 100)}%`,
                  backgroundColor: SEVERITY_COLOR[level],
                }}
              />
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const COLUMNS = 'grid grid-cols-1 md:grid-cols-[1.2fr_1fr_1fr] gap-8';

// Each description collapses to two lines (the mockup shows them truncated)
// and expands on click. Spelled out in full, fourteen rows of paragraphs push
// the platform lists and index bars far apart and make it hard to read across.
const ThreatRow = ({ threat }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`${COLUMNS} py-6 border-b border-gray-200`}>
      <div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="w-full text-left"
        >
          <span className="flex items-start justify-between gap-2">
            <span className="text-lg font-bold text-black">{threat.title} -</span>
            <span aria-hidden="true" className="mt-1.5 flex-shrink-0 text-xs leading-none text-gray-500">
              {open ? '^' : 'v'}
            </span>
          </span>
          {/* No `block` on the collapsed state: it sets display:block, and
              -webkit-line-clamp only takes effect on display:-webkit-box,
              which line-clamp-2 supplies itself. */}
          <span
            className={`mt-1 text-base text-black leading-relaxed ${
              open ? 'block' : 'line-clamp-2'
            }`}
          >
            {threat.description}
          </span>
        </button>
      </div>
      <PlatformList ratings={threat.ratings} />
      <ThreatIndex ratings={threat.ratings} />
    </div>
  );
};

const CategorySection = ({ group }) => (
  <section className="mt-10">
    <h2 className="text-2xl font-bold text-black uppercase tracking-wide mb-4">
      {group.category}
    </h2>
    <div className={`${COLUMNS} border-b border-black pb-2`}>
      <p className="text-lg font-bold text-black">Scams</p>
      <p className="text-lg font-bold text-black">Impacted Platforms Risk Rating</p>
      <p className="text-lg font-bold text-black">Overall Threat Index</p>
    </div>
    {group.threats.map((t) => (
      <ThreatRow key={t.title} threat={t} />
    ))}
  </section>
);

const ThreatMatrix = () => (
  <div className="mt-6">
    <div className="flex items-end justify-between gap-4 flex-wrap">
      <p className="text-sm text-gray-600 max-w-2xl">
        How each scam presents across the major platforms. Severity reflects how damaging the scam
        is there, not how likely you are to meet it today. The threat index shows the share of
        platforms at each level; the overall rating is the highest level present.
      </p>
      <Legend />
    </div>
    {SOCIAL_MEDIA_MATRIX.map((group) => (
      <CategorySection key={group.category} group={group} />
    ))}
  </div>
);

// The tactics behind these scams live on their own page.
const PlaybookLink = () => (
  <p className="mt-14 border-t border-black pt-6 text-base text-black">
    How these scams work on you:{' '}
    <Link to="/dashboard/abuse-playbook" className="text-blue-600 hover:underline">
      The Abuse Playbook &rarr;
    </Link>
  </p>
);

const SocialMediaIQPage = () => (
  <HubPageLayout>
    <PageHeader />
    <ThreatMatrix />
    <PlaybookLink />
  </HubPageLayout>
);

export default SocialMediaIQPage;
